import { action, makeObservable, observable, runInAction } from "mobx";
import ErrorStore from "../../common/stores/ErrorStore";
import ProjectsStore from "../../common/stores/ProjectsStore";
import WorkbenchService from "../services/WorkbenchService";
import {
    WbAction,
    WbActivity,
    WbCaseSummary,
    WbDocument,
    WbSubject,
    WbTask,
    ContractStage,
} from "../types/WbTypes";
import { TaskService } from "../../tasks/services";

export class WidgetState {
    isLoading: boolean = false;
    hasData: boolean = false;
    error: string | null = null;
    lastQuery: string | null = null;

    constructor() {
        makeObservable(this, {
            isLoading: observable,
            hasData: observable,
            error: observable,
            lastQuery: observable,
        });
    }
}

export default class WorkbenchStore {
    isLoading: boolean = false;
    isInitialLoading: boolean = false;
    error: string | null = null;
    searchInput: string = "";
    isSubjectSearchLoading: boolean = false;
    subjectSearchError: string | null = null;
    subjectSuggestions: WbSubject[] = [];
    subjectSuggestionsTotalCount: number = 0;
    selectedSubject: WbSubject | null = null;

    subjectInfoWidget: WidgetState = new WidgetState();
    tasksWidget: WidgetState = new WidgetState();
    documentsWidget: WidgetState = new WidgetState();
    actionsHistoryWidget: WidgetState = new WidgetState();
    caseSummaryWidget: WidgetState = new WidgetState();
    activityWidget: WidgetState = new WidgetState();
    activityData: WbActivity[] = [];
    private activityRequestId = 0;

    subjectData: WbSubject | null = null;
    isStageSaving = false;
    stageNeedsRefresh = false;
    stageError: string | null = null;
    private stageRequestId = 0;
    tasksData: WbTask[] = [];
    documentsData: WbDocument[] = [];
    actionsHistoryData: WbAction[] = [];
    caseSummaryData: WbCaseSummary | null = null;

    private subjectSearchDebounceHandle: ReturnType<typeof setTimeout> | null =
        null;
    private subjectSearchRequestId = 0;

    constructor(
        private errorStore: ErrorStore,
        private projectsStore: ProjectsStore,
        private wbService: WorkbenchService,
        private tasksService: TaskService,
    ) {
        makeObservable(this, {
            isLoading: observable,
            isInitialLoading: observable,
            error: observable,
            searchInput: observable,
            isSubjectSearchLoading: observable,
            subjectSearchError: observable,
            subjectSuggestions: observable,
            subjectSuggestionsTotalCount: observable,
            selectedSubject: observable,
            subjectInfoWidget: observable,
            tasksWidget: observable,
            documentsWidget: observable,
            actionsHistoryWidget: observable,
            caseSummaryWidget: observable,
            activityWidget: observable,
            activityData: observable,
            loadActivityContent: action.bound,
            subjectData: observable,
            isStageSaving: observable,
            stageNeedsRefresh: observable,
            stageError: observable,
            changeContractStage: action.bound,
            refreshContractStage: action.bound,
            tasksData: observable,
            documentsData: observable,
            actionsHistoryData: observable,
            caseSummaryData: observable,
            setIsLoading: action,
            setIsInitialLoading: action,
            setError: action,
            setSearchInput: action.bound,
            searchSubjectsNow: action.bound,
            updateWidgetState: action,
            loadInitialData: action.bound,
            clearSelection: action.bound,
            selectSubjectByValue: action.bound,
            selectSubject: action.bound,
            loadSubjectById: action.bound,
            refreshSubjectFacts: action.bound,
            loadAllWidgetContent: action.bound,
            loadTasksContent: action.bound,
            loadDocumentsContent: action.bound,
            loadActionsHistoryContent: action.bound,
            loadCaseSummaryContent: action.bound,
            handleAttachmentDownload: action.bound,
        });
    }

    get projects() {
        return this.projectsStore.projects ?? [];
    }

    async refreshContractStage() {
        const subjectId = this.subjectData?.id;
        if (!subjectId || this.isStageSaving) return;
        this.isStageSaving = true;
        const requestId = ++this.stageRequestId;
        try {
            const response = await this.wbService.fetchSubject(subjectId);
            if (
                requestId !== this.stageRequestId ||
                this.subjectData?.id !== subjectId
            )
                return;
            runInAction(() => {
                if (
                    requestId !== this.stageRequestId ||
                    this.subjectData?.id !== subjectId
                )
                    return;
                if (response.isOk()) {
                    this.applyStageSubject(response.value);
                    this.stageNeedsRefresh = false;
                    this.stageError = null;
                    this.subjectInfoWidget.error = null;
                } else {
                    this.stageNeedsRefresh = true;
                    this.stageError =
                        "Could not confirm the saved stage. Retry loading before changing it.";
                }
            });
            if (response.isOk()) {
                await this.loadActivityContent(response.value);
            }
        } catch {
            runInAction(() => {
                if (
                    requestId !== this.stageRequestId ||
                    this.subjectData?.id !== subjectId
                )
                    return;
                this.stageNeedsRefresh = true;
                this.stageError =
                    "Could not confirm the saved stage. Retry loading before changing it.";
            });
        } finally {
            runInAction(() => {
                if (requestId === this.stageRequestId) {
                    this.isStageSaving = false;
                }
            });
        }
    }

    async changeContractStage(stage: ContractStage): Promise<boolean> {
        const subject = this.subjectData;
        if (
            !subject ||
            subject.kind !== "Contract" ||
            this.isStageSaving ||
            this.stageNeedsRefresh ||
            subject.contractStage === stage
        )
            return false;
        const requestId = ++this.stageRequestId;
        this.isStageSaving = true;
        this.stageError = null;
        // Keep the last persisted stage visible until the save is confirmed.
        try {
            const response = await this.wbService.changeContractStage(
                subject.id,
                stage,
                subject.contractStageVersion ?? 0,
            );
            if (
                requestId !== this.stageRequestId ||
                this.subjectData?.id !== subject.id
            )
                return false;
            if (response.isOk()) {
                runInAction(() => {
                    this.applyStageSubject(response.value);
                });
                await this.loadActivityContent(response.value);
                return true;
            }
            const conflict = response.error.status === 409;
            // Read after failures too: a lost response can hide a successful write.
            const current = await this.wbService.fetchSubject(subject.id);
            if (
                requestId !== this.stageRequestId ||
                this.subjectData?.id !== subject.id
            )
                return false;
            runInAction(() => {
                if (
                    requestId !== this.stageRequestId ||
                    this.subjectData?.id !== subject.id
                )
                    return;
                if (current.isOk()) {
                    this.applyStageSubject(current.value);
                    this.stageError = conflict
                        ? "The stage changed since you opened it. The latest stage is shown; select again to retry."
                        : "The save could not be confirmed. The current saved stage is shown.";
                } else {
                    this.stageNeedsRefresh = true;
                    this.stageError =
                        "Could not confirm the saved stage. Retry loading before changing it.";
                }
            });
            if (current.isOk()) {
                await this.loadActivityContent(current.value);
            }
        } catch {
            runInAction(() => {
                if (
                    requestId !== this.stageRequestId ||
                    this.subjectData?.id !== subject.id
                )
                    return;
                this.stageNeedsRefresh = true;
                this.stageError =
                    "Could not confirm the saved stage. Retry loading before changing it.";
            });
        } finally {
            runInAction(() => {
                if (requestId === this.stageRequestId) {
                    this.isStageSaving = false;
                }
            });
        }
        return false;
    }

    private applyStageSubject(subject: WbSubject) {
        const merge = (current: WbSubject): WbSubject =>
            current.id === subject.id &&
            (subject.contractStageVersion ?? 0) >=
                (current.contractStageVersion ?? 0)
                ? {
                      ...current,
                      contractStage: subject.contractStage,
                      contractStageVersion: subject.contractStageVersion,
                  }
                : current;
        if (this.subjectData) this.subjectData = merge(this.subjectData);
        if (this.selectedSubject)
            this.selectedSubject = merge(this.selectedSubject);
        this.subjectSuggestions = this.subjectSuggestions.map(merge);
    }

    getSubjectOptionValue(subject: WbSubject) {
        return `${subject.kind} - ${subject.identifier}`;
    }

    setIsLoading(isLoading: boolean) {
        this.isLoading = isLoading;
    }

    setIsInitialLoading(isInitialLoading: boolean) {
        this.isInitialLoading = isInitialLoading;
    }

    setError(error: string | null) {
        this.error = error;
    }

    setSearchInput(value: string) {
        this.searchInput = value;
        this.subjectSearchError = null;

        if (!value.trim()) {
            this.clearSubjectSearch();
            this.clearSelection();
            return;
        }

        this.scheduleSubjectSearch(value);
    }

    async searchSubjectsNow() {
        const trimmedQuery = this.searchInput.trim();

        if (this.subjectSearchDebounceHandle) {
            clearTimeout(this.subjectSearchDebounceHandle);
            this.subjectSearchDebounceHandle = null;
        }

        if (trimmedQuery.length < 2) {
            this.clearSubjectSearch();
            return;
        }

        const requestId = ++this.subjectSearchRequestId;
        this.isSubjectSearchLoading = true;
        this.subjectSearchError = null;

        await this.performSubjectSearch(trimmedQuery, requestId);
    }

    updateWidgetState(
        widgetName:
            | "subjectInfoWidget"
            | "tasksWidget"
            | "documentsWidget"
            | "actionsHistoryWidget"
            | "caseSummaryWidget"
            | "activityWidget",
        updates: Partial<WidgetState>,
    ) {
        const widget = this[widgetName] as WidgetState;
        Object.assign(widget, updates);
    }

    async loadInitialData() {
        this.setIsInitialLoading(true);
        try {
            await this.projectsStore.loadProjects();
        } catch (err) {
            this.setError("Failed to load initial data");
            this.errorStore.addBasicError(err as Error);
        } finally {
            this.setIsInitialLoading(false);
        }
    }

    clearSelection() {
        ++this.activityRequestId;
        this.activityData = [];
        this.updateWidgetState("activityWidget", {
            isLoading: false,
            error: null,
            hasData: false,
        });
        ++this.factsRequestId;
        ++this.stageRequestId;
        this.isStageSaving = false;
        this.stageNeedsRefresh = false;
        this.stageError = null;
        this.selectedSubject = null;
        this.subjectData = null;
        this.tasksData = [];
        this.documentsData = [];
        this.actionsHistoryData = [];
        this.caseSummaryData = null;

        this.updateWidgetState("subjectInfoWidget", {
            isLoading: false,
            hasData: false,
            error: null,
            lastQuery: null,
        });
        this.updateWidgetState("tasksWidget", {
            isLoading: false,
            hasData: false,
            error: null,
            lastQuery: null,
        });
        this.updateWidgetState("documentsWidget", {
            isLoading: false,
            hasData: false,
            error: null,
            lastQuery: null,
        });
        this.updateWidgetState("actionsHistoryWidget", {
            isLoading: false,
            hasData: false,
            error: null,
            lastQuery: null,
        });
        this.updateWidgetState("caseSummaryWidget", {
            isLoading: false,
            hasData: false,
            error: null,
            lastQuery: null,
        });
    }

    async selectSubjectByValue(optionValue: string) {
        const subject =
            this.subjectSuggestions.find(
                (item) => this.getSubjectOptionValue(item) === optionValue,
            ) ??
            (this.selectedSubject &&
            this.getSubjectOptionValue(this.selectedSubject) === optionValue
                ? this.selectedSubject
                : null);

        if (!subject) {
            return;
        }

        await this.selectSubject(subject);
    }

    async selectSubject(subject: WbSubject) {
        ++this.factsRequestId;
        if (this.subjectData?.id !== subject.id) this.clearSelection();
        const subjectLabel = this.getSubjectOptionValue(subject);

        runInAction(() => {
            this.selectedSubject = subject;
            this.subjectData = subject;
            this.searchInput = subjectLabel;
            this.subjectSuggestions = [];
            this.subjectSuggestionsTotalCount = 0;
            this.subjectSearchError = null;
            this.updateWidgetState("subjectInfoWidget", {
                isLoading: false,
                hasData: true,
                error: null,
                lastQuery: subjectLabel,
            });
        });

        await this.loadAllWidgetContent(subject);
    }

    private factsRequestId = 0;

    async refreshSubjectFacts(subjectId: string) {
        const requestId = ++this.factsRequestId;
        try {
            const response = await this.wbService.fetchSubject(subjectId);
            if (requestId !== this.factsRequestId) return;
            response
                .map((subject) => {
                    runInAction(() => {
                        const current = this.subjectData;
                        if (
                            current?.id === subjectId &&
                            (current.contractStageVersion ?? 0) >
                                (subject.contractStageVersion ?? 0)
                        ) {
                            subject = {
                                ...subject,
                                contractStage: current.contractStage,
                                contractStageVersion:
                                    current.contractStageVersion,
                            };
                        }
                        if (this.subjectData?.id === subjectId)
                            this.subjectData = subject;
                        if (this.selectedSubject?.id === subjectId)
                            this.selectedSubject = subject;
                        this.subjectSuggestions = this.subjectSuggestions.map(
                            (item) => (item.id === subjectId ? subject : item),
                        );
                    });
                })
                .mapErr((err) => this.errorStore.addError(err.data));
        } catch (err) {
            if (requestId === this.factsRequestId)
                this.errorStore.addBasicError(err as Error);
        }
    }

    async loadSubjectById(
        subjectId: string,
        initialSubject?: WbSubject | null,
    ) {
        if (this.subjectData?.id !== subjectId) this.clearSelection();
        const initialSubjectMatches = initialSubject?.id === subjectId;

        if (initialSubjectMatches && initialSubject) {
            const subjectLabel = this.getSubjectOptionValue(initialSubject);

            runInAction(() => {
                this.selectedSubject = initialSubject;
                this.subjectData = initialSubject;
                this.searchInput = subjectLabel;
                this.subjectSuggestions = [];
                this.subjectSuggestionsTotalCount = 0;
                this.subjectSearchError = null;
                this.updateWidgetState("subjectInfoWidget", {
                    isLoading: true,
                    hasData: true,
                    error: null,
                    lastQuery: subjectLabel,
                });
            });
        } else {
            runInAction(() => {
                this.clearSelection();
                this.updateWidgetState("subjectInfoWidget", {
                    isLoading: true,
                    hasData: false,
                    error: null,
                    lastQuery: subjectId,
                });
            });
        }

        try {
            const response = await this.wbService.fetchSubject(subjectId);
            let loadedSubject: WbSubject | null = null;

            response
                .map((subject) => {
                    const subjectLabel = this.getSubjectOptionValue(subject);
                    loadedSubject = subject;

                    runInAction(() => {
                        this.selectedSubject = subject;
                        this.subjectData = subject;
                        this.searchInput = subjectLabel;
                        this.subjectSuggestions = [];
                        this.subjectSuggestionsTotalCount = 0;
                        this.subjectSearchError = null;
                        this.updateWidgetState("subjectInfoWidget", {
                            isLoading: false,
                            hasData: true,
                            error: null,
                            lastQuery: subjectLabel,
                        });
                    });
                })
                .mapErr((err) => {
                    runInAction(() => {
                        this.updateWidgetState("subjectInfoWidget", {
                            isLoading: false,
                            hasData: initialSubjectMatches,
                            error: "Failed to load subject",
                        });
                    });
                    this.errorStore.addError(err.data);
                });

            if (loadedSubject) {
                await this.loadAllWidgetContent(loadedSubject);
            }
        } catch (err) {
            runInAction(() => {
                this.updateWidgetState("subjectInfoWidget", {
                    isLoading: false,
                    hasData: initialSubjectMatches,
                    error: "Failed to load subject",
                });
            });
            this.errorStore.addBasicError(err as Error);
        }
    }

    async loadAllWidgetContent(subject: WbSubject) {
        await Promise.all([
            this.loadTasksContent(subject),
            this.loadDocumentsContent(subject),
            this.loadActionsHistoryContent(subject),
            this.loadCaseSummaryContent(subject),
            this.loadActivityContent(subject),
        ]);
    }

    async loadActivityContent(subject: WbSubject) {
        const requestId = ++this.activityRequestId;
        if (subject.kind !== "Contract") {
            this.activityData = [];
            this.updateWidgetState("activityWidget", {
                isLoading: false,
                error: null,
                hasData: false,
            });
            return;
        }
        this.updateWidgetState("activityWidget", {
            isLoading: true,
            error: null,
        });
        try {
            const response = await this.wbService.fetchSubjectActivity(
                subject.id,
            );
            runInAction(() => {
                if (
                    requestId !== this.activityRequestId ||
                    this.subjectData?.id !== subject.id
                )
                    return;
                if (response.isOk()) {
                    this.activityData = response.value;
                    this.updateWidgetState("activityWidget", {
                        isLoading: false,
                        hasData: response.value.length > 0,
                    });
                } else {
                    this.updateWidgetState("activityWidget", {
                        isLoading: false,
                        error: "Could not load activity.",
                    });
                }
            });
        } catch {
            runInAction(() => {
                if (
                    requestId !== this.activityRequestId ||
                    this.subjectData?.id !== subject.id
                )
                    return;
                this.updateWidgetState("activityWidget", {
                    isLoading: false,
                    error: "Could not load activity.",
                });
            });
        }
    }

    async loadTasksContent(subject: WbSubject) {
        const subjectLabel = this.getSubjectOptionValue(subject);
        this.updateWidgetState("tasksWidget", {
            isLoading: true,
            error: null,
            lastQuery: subjectLabel,
        });

        try {
            const [tasksResponse] = await Promise.all([
                this.wbService.fetchSubjectTasks(subject.id),
                this.wait(500),
            ]);

            if (!this.isSubjectSelectionCurrent(subject.id)) {
                return;
            }

            tasksResponse
                .map((data) => {
                    runInAction(() => {
                        if (!this.isSubjectSelectionCurrent(subject.id)) {
                            return;
                        }

                        this.tasksData = data;
                        this.updateWidgetState("tasksWidget", {
                            isLoading: false,
                            hasData: data.length > 0,
                        });
                    });
                })
                .mapErr((err) => {
                    if (!this.isSubjectSelectionCurrent(subject.id)) {
                        return;
                    }

                    this.updateWidgetState("tasksWidget", {
                        isLoading: false,
                        error: "Failed to load tasks",
                    });
                    this.errorStore.addError(err.data);
                });
        } catch (err) {
            if (!this.isSubjectSelectionCurrent(subject.id)) {
                return;
            }

            this.updateWidgetState("tasksWidget", {
                isLoading: false,
                error: "Failed to load tasks",
            });
            this.errorStore.addBasicError(err as Error);
        }
    }

    async loadDocumentsContent(subject: WbSubject) {
        const subjectLabel = this.getSubjectOptionValue(subject);
        this.updateWidgetState("documentsWidget", {
            isLoading: true,
            error: null,
            lastQuery: subjectLabel,
        });

        try {
            const [documentsResponse] = await Promise.all([
                this.wbService.fetchSubjectDocuments(subject.id),
                this.wait(500),
            ]);

            if (!this.isSubjectSelectionCurrent(subject.id)) {
                return;
            }

            documentsResponse
                .map((data) => {
                    runInAction(() => {
                        if (!this.isSubjectSelectionCurrent(subject.id)) {
                            return;
                        }

                        this.documentsData = data;
                        this.updateWidgetState("documentsWidget", {
                            isLoading: false,
                            hasData: data.length > 0,
                        });
                    });
                })
                .mapErr((err) => {
                    if (!this.isSubjectSelectionCurrent(subject.id)) {
                        return;
                    }

                    this.updateWidgetState("documentsWidget", {
                        isLoading: false,
                        error: "Failed to load documents",
                    });
                    this.errorStore.addError(err.data);
                });
        } catch (err) {
            if (!this.isSubjectSelectionCurrent(subject.id)) {
                return;
            }

            this.updateWidgetState("documentsWidget", {
                isLoading: false,
                error: "Failed to load documents",
            });
            this.errorStore.addBasicError(err as Error);
        }
    }

    async loadActionsHistoryContent(subject: WbSubject) {
        const subjectLabel = this.getSubjectOptionValue(subject);
        this.updateWidgetState("actionsHistoryWidget", {
            isLoading: true,
            error: null,
            lastQuery: subjectLabel,
        });

        try {
            const [actionsResponse] = await Promise.all([
                this.wbService.fetchSubjectActions(subject.id),
                this.wait(500),
            ]);

            if (!this.isSubjectSelectionCurrent(subject.id)) {
                return;
            }

            actionsResponse
                .map((data) => {
                    runInAction(() => {
                        if (!this.isSubjectSelectionCurrent(subject.id)) {
                            return;
                        }

                        this.actionsHistoryData = data;
                        this.updateWidgetState("actionsHistoryWidget", {
                            isLoading: false,
                            hasData: data.length > 0,
                        });
                    });
                })
                .mapErr((err) => {
                    if (!this.isSubjectSelectionCurrent(subject.id)) {
                        return;
                    }

                    this.updateWidgetState("actionsHistoryWidget", {
                        isLoading: false,
                        error: "Failed to load actions history",
                    });
                    this.errorStore.addError(err.data);
                });
        } catch (err) {
            if (!this.isSubjectSelectionCurrent(subject.id)) {
                return;
            }

            this.updateWidgetState("actionsHistoryWidget", {
                isLoading: false,
                error: "Failed to load actions history",
            });
            this.errorStore.addBasicError(err as Error);
        }
    }

    async loadCaseSummaryContent(subject: WbSubject) {
        const subjectLabel = this.getSubjectOptionValue(subject);
        this.updateWidgetState("caseSummaryWidget", {
            isLoading: true,
            error: null,
            lastQuery: subjectLabel,
        });

        try {
            const [caseSummaryResponse] = await Promise.all([
                this.wbService.fetchSubjectCaseSummary(subject.id),
                this.wait(500),
            ]);

            if (!this.isSubjectSelectionCurrent(subject.id)) {
                return;
            }

            caseSummaryResponse
                .map((data) => {
                    runInAction(() => {
                        if (!this.isSubjectSelectionCurrent(subject.id)) {
                            return;
                        }

                        this.caseSummaryData = data;
                        this.updateWidgetState("caseSummaryWidget", {
                            isLoading: false,
                            hasData: Boolean(data.snapshotId),
                        });
                    });
                })
                .mapErr((err) => {
                    if (!this.isSubjectSelectionCurrent(subject.id)) {
                        return;
                    }

                    this.updateWidgetState("caseSummaryWidget", {
                        isLoading: false,
                        error: "Failed to load case summary",
                    });
                    this.errorStore.addError(err.data);
                });
        } catch (err) {
            if (!this.isSubjectSelectionCurrent(subject.id)) {
                return;
            }

            this.updateWidgetState("caseSummaryWidget", {
                isLoading: false,
                error: "Failed to load case summary",
            });
            this.errorStore.addBasicError(err as Error);
        }
    }

    async handleAttachmentDownload(packageId: string) {
        try {
            await this.tasksService.handleDownload([packageId]);
        } catch (err) {
            this.errorStore.addBasicError(err);
        }
    }

    wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

    private isSubjectSelectionCurrent(subjectId: string) {
        return this.selectedSubject?.id === subjectId;
    }

    private clearSubjectSearch() {
        if (this.subjectSearchDebounceHandle) {
            clearTimeout(this.subjectSearchDebounceHandle);
            this.subjectSearchDebounceHandle = null;
        }

        this.subjectSearchRequestId++;
        this.isSubjectSearchLoading = false;
        this.subjectSearchError = null;
        this.subjectSuggestions = [];
        this.subjectSuggestionsTotalCount = 0;
    }

    private scheduleSubjectSearch(query: string) {
        const trimmedQuery = query.trim();

        if (trimmedQuery.length < 2) {
            this.clearSubjectSearch();
            return;
        }

        if (this.subjectSearchDebounceHandle) {
            clearTimeout(this.subjectSearchDebounceHandle);
        }

        const requestId = ++this.subjectSearchRequestId;
        this.isSubjectSearchLoading = true;

        this.subjectSearchDebounceHandle = setTimeout(() => {
            this.subjectSearchDebounceHandle = null;
            void this.performSubjectSearch(trimmedQuery, requestId);
        }, 300);
    }

    private async performSubjectSearch(query: string, requestId: number) {
        try {
            const response = await this.wbService.searchSubjects(query);

            if (requestId !== this.subjectSearchRequestId) {
                return;
            }

            response
                .map((data) => {
                    runInAction(() => {
                        if (requestId !== this.subjectSearchRequestId) {
                            return;
                        }

                        this.subjectSuggestions = data.items;
                        this.subjectSuggestionsTotalCount = data.totalCount;
                        this.isSubjectSearchLoading = false;
                        this.subjectSearchError = null;
                    });
                })
                .mapErr((err) => {
                    runInAction(() => {
                        if (requestId !== this.subjectSearchRequestId) {
                            return;
                        }

                        this.subjectSuggestions = [];
                        this.subjectSuggestionsTotalCount = 0;
                        this.isSubjectSearchLoading = false;
                        this.subjectSearchError = "Failed to search subjects";
                    });
                    this.errorStore.addError(err.data);
                });
        } catch (err) {
            if (requestId !== this.subjectSearchRequestId) {
                return;
            }

            runInAction(() => {
                this.subjectSuggestions = [];
                this.subjectSuggestionsTotalCount = 0;
                this.isSubjectSearchLoading = false;
                this.subjectSearchError = "Failed to search subjects";
            });
            this.errorStore.addBasicError(err as Error);
        }
    }
}
